import { ChangeDetectorRef, Component, computed, effect, inject, output, signal } from '@angular/core';
import { SignalsStoreService } from '../signals-store.service';
import { SidebarService } from './sidebar.service';
import { SidebarCheckList, SidebarMenu } from './sidebar.types';
import { CommonModule } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { CategorySelected } from '../types/common.types';
import { ProductsService } from '../../product/products.service';
import { ResolutionService } from '../resolution.service';
import { goToTopPage } from '../common/utils';
import { ShopComponent } from '../../shop/shop.component';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {

  //#region Services

  signalsStore = inject(SignalsStoreService);

  sidebarService = inject(SidebarService);

  #resolutionService = inject(ResolutionService);

  #productsService = inject(ProductsService);

  #cdr = inject(ChangeDetectorRef);

  //#endregion

  //#region Properties

  hasGlobalMessages = computed(() => !!this.signalsStore.globalMessages().length);

  isMobile = computed(() => this.#resolutionService.isMobile());

  isInSearchMode = computed(() => this.signalsStore.isInSearchMode());

  /**
   * Which groups are expanded, by `key`.
   *
   * The Bootstrap accordion this replaces opened every group at once and kept them open — on a
   * phone that is six headers and a hundred and fifty producers between the shopper and the
   * Apply button. The design opens one group at a time on mobile and leaves desktop free to
   * open as many as it likes, so the set is tracked here rather than in the markup.
   *
   * Starts empty, and nothing opens a group on load: the client asked for every group shut so
   * the shopper sees the whole list of options first and opens one with the caret (DEV-11323,
   * ELIO-3875 on 2026-10-16). Producers used to open itself here, which on the rail buried the
   * five groups under it behind a hundred and fifty checkboxes.
   */
  #openGroups = signal<ReadonlySet<string>>(new Set());

  applyLabel = computed(() => {
    const n = this.sidebarService.activeFilterCount();
    if (!n) return 'Apply Filters';
    return `Apply ${n} ${n === 1 ? 'Filter' : 'Filters'}`;
  });

  //#endregion

  //#region Constructor

  outIsMenuReady = output<boolean>();

  constructor(public shopComponent: ShopComponent) {
    toObservable(this.sidebarService.sidebar).subscribe((res) => {
      if (!res || res.length === 0) return;
      setTimeout(() => {
        this.outIsMenuReady.emit(true);
      }, 100);
    });

    toObservable(this.signalsStore.categoryInViewPort)
      .subscribe(obs => this.#changeCategoryInViewPort(obs));

    // The phone's panel is a modal with Cancel / Apply, so what it collects has to be staged
    // until one of the two is pressed. Opening it is what starts that; dismissing it any other
    // way (the header's own toggle, a navigation) has to drop the staged ticks, or they would
    // be sitting there, ticked but unapplied, the next time it opens.
    effect(() => {
      const open = this.signalsStore.isSidebarOpen();
      if (!this.isMobile()) return;
      if (open) this.sidebarService.beginStaging();
      else if (this.sidebarService.isStaging()) this.sidebarService.cancelStaging();
    });
  }


  //#endregion

  //#region Methods

  goToShopSection(item: SidebarMenu, event: Event) {
    this.#productsService.goToSection(item, event);
  }

  /**
   * Whether a subcategory shows the children under it: only while the shop is inside it —
   * browsing the subcategory itself, or one of its children.
   *
   * Every branch opened at once is how the rail looked the first time production had three
   * levels. Philly Foodworks splits most of Produce into Local / Non-Local, so the rail spent
   * a screen and a half listing places the shopper is not, to mark the one they are
   * (DEV-11323).
   */
  showsLeaves(branch: SidebarMenu): boolean {
    return !!branch.checked || !!branch.sublist?.some(leaf => leaf.checked);
  }

  //#region Filter groups

  isGroupOpen(item: SidebarCheckList) {
    return this.#openGroups().has(item.key);
  }

  toggleGroup(item: SidebarCheckList) {
    this.#openGroups.update(current => {
      const next = new Set(this.isMobile() ? [] : current);
      if (current.has(item.key)) next.delete(item.key);
      else next.add(item.key);
      return next;
    });
  }

  /**
   * Whether this group draws its options as icon pills.
   *
   * The backend's own word first, when it ever sends one. Otherwise the icons decide: a group
   * whose options carry artwork is one the design draws as pills, and one that carries none —
   * `producers`, which is a list of farm names — is a checkbox list. Checked against the live
   * production catalogue that puts Dietary, Production and Packaging on the right side with
   * `producers`; only Mileage disagrees, and `displayStyle` is how that gets settled.
   *
   * `some`, not `every`: Packaging ships icons for three of its five tags, and a row that
   * switched shape per option would be the worst of both. An option with no icon simply
   * renders without one.
   */
  drawsPills(item: SidebarCheckList) {
    if (item.displayStyle === 'pill') return true;
    if (item.displayStyle === 'checkbox') return false;
    return !!item.sublist?.some(option => !!option.icon);
  }

  toggleOption(item: SidebarCheckList, sublist: SidebarCheckList) {
    if (sublist.disabled) return;
    // handleCheckChange reads `checked` to decide whether it is adding or removing, so the flag
    // moves first. ngModel used to do this; a native checkbox does not write back on its own
    // when it is bound with [checked].
    sublist.checked = !sublist.checked;
    this.sidebarService.handleCheckChange(item, sublist);
  }

  applyFilters() {
    this.sidebarService.applyStaging();
    this.signalsStore.isSidebarOpen.set(false);
  }

  cancelFilters() {
    this.sidebarService.cancelStaging();
    this.signalsStore.isSidebarOpen.set(false);
  }

  //#endregion

  #changeCategoryInViewPort(obj: CategorySelected | null) {

    const { categoryId, subCategoryId, subCategorySonId } = obj ?? {};

    if (!categoryId || !subCategoryId)
      return;

    this.#resetOpenDetails();
    this.#selectSubCategory([categoryId, subCategoryId, subCategorySonId ?? 0], this.sidebarService.sidebarMenu());

    // This runs from a toObservable(categoryInViewPort) subscription that mutates plain
    // `item.checked` flags — no template signal read — so nothing marks this view dirty.
    // The parent ShopComponent is OnPush, which makes Angular skip this whole subtree during
    // change detection, so the active-item highlight would only refresh on the next event.
    // Mark for check so the highlight updates immediately.
    this.#cdr.markForCheck();
  }

  #resetOpenDetails() {

    const f = (item: SidebarMenu) => {
      item.checked = false;
      if (item.sublist?.length)
        item.sublist.forEach(x => f(x))
    }

    this.sidebarService
      .sidebarMenu()
      .forEach(x => f(x));
  }

  #selectSubCategory(ids: number[], array: SidebarMenu[]) {

    const id = ids.shift();
    const item = array.find(x => x.id === id);

    if (!item)
      return;

    item.checked = true;

    if (!item.sublist?.length)
      return;

    this.#selectSubCategory(ids, item.sublist);
  }

  toggleSidebar() {
    this.signalsStore.isSidebarOpen.update(currentValue => !currentValue);
  }

  goToTop() { goToTopPage() };

  //#endregion
}
